import React, { useCallback, useMemo } from 'react';
import WarningIcon from '../ui/WarningIcon';
import TerminalGroupChip from '../ui/TerminalGroupChip';
import CableColorRect from '../ui/CableColorRect';
import CatalogAssignmentIndicator from '../ui/CatalogAssignmentIndicator';
import { Icons } from '../icons/Icons';
import { formatSezione, getCableDisplayName, getCableWidthForTessera, getTerminalGroupForEndpoint } from '../../utils/cableLogic';
import { translateColorForDisplay } from '../../utils/colorUtils';
import { getCableCatalogStatus } from '../../utils/cableCatalog';
import MultipolarTessera from './MultipolarTessera';

const getRelatedCablesMemoKey = (cables = []) => cables
  .map(relatedCable => JSON.stringify({
    originalIndex: relatedCable.originalIndex,
    siglatura: relatedCable.siglatura,
    colore: relatedCable.colore,
    sezione: relatedCable.sezione,
    lunghezza: relatedCable.lunghezza,
    terminaleA: relatedCable.terminaleA,
    terminaleB: relatedCable.terminaleB,
    collegamentoA: relatedCable.collegamentoA,
    collegamentoB: relatedCable.collegamentoB,
    guaina: relatedCable.guaina
  }))
  .join('|');

const CableTessera = React.memo(({
  cable,
  displaySiglatura,
  onCableClick,
  onEditCable,
  onEditMultipolarCable,
  onDeleteCable,
  onDeleteMultipolarCable,
  showSiglatura = true,
  clickable = true,
  settings = {},
  isDragEnabled = false,
  validateCable,
  isDragging = false,
  isDragOver = false,
  hasIssues = false,
  isHovered = false,
  editingIndex = -1,
  editingMultipolarIndex = -1,
  showDeleteButton = false,
  onToggleDelete,
  isSelectionModeEnabled = false,
  selectedCables = new Set(),
  onToggleSelection,
  handleDragStart,
  handleDragEnd,
  handleDragOver,
  handleDragEnter,
  handleDragLeave,
  handleDrop,
  draggedIndex = null,
  dragOverIndex = null,
  availableSheaths = [],
  relatedCables = [],
  terminalGroups = null,
  hideSheathBadge = false
}) => {
  const isMultipolar = Boolean(cable.poli && Array.isArray(cable.poli));
  const representativeData = isMultipolar ? cable.poli[0] : cable;
  const validation = useMemo(() => validateCable(cable), [cable, validateCable]);
  const actualHasIssues = hasIssues || !validation.isValid;
  const catalogStatus = getCableCatalogStatus(cable);
  const isBeingEdited = isMultipolar ? editingMultipolarIndex === cable.originalIndex : editingIndex === cable.originalIndex;
  const shouldShowDelete = Boolean(showDeleteButton || isBeingEdited);

  const containerClasses = useMemo(() => {
    const baseClasses = ['border-2', 'rounded-lg', 'transition-all', 'duration-200'];

    if (isDragEnabled) baseClasses.push('cursor-move');
    else if (clickable) baseClasses.push('cursor-pointer');

    if (actualHasIssues) {
      baseClasses.push('bg-amber-50', 'border-amber-300', 'hover:bg-amber-100', 'hover:border-amber-400');
    } else if (isHovered) {
      baseClasses.push('bg-blue-50', 'border-blue-300', '-translate-y-px', 'shadow-[0_4px_8px_rgba(59,130,246,0.3)]');
    } else {
      baseClasses.push('bg-gray-50', 'border-gray-200', 'hover:bg-gray-100');
      if (isBeingEdited) baseClasses.push('bg-gray-100 shadow-sm');
    }

    if (isDragEnabled && !actualHasIssues) {
      baseClasses.push('hover:bg-blue-50', 'hover:border-blue-300');
    }

    if (isDragging) {
      baseClasses.push('opacity-50', 'transform', 'scale-95', 'border-blue-400');
    }

    if (isDragOver) {
      baseClasses.push('border-blue-500', 'bg-blue-100', '-translate-y-px', 'shadow-[0_4px_8px_rgba(59,130,246,0.3)]');
    }

    if (actualHasIssues && !isDragging && !isDragOver && !isHovered) {
      baseClasses.push('shadow-[0_2px_4px_rgba(245,158,11,0.2)]');
    }

    return baseClasses.join(' ');
  }, [isDragEnabled, clickable, actualHasIssues, isHovered, isBeingEdited, isDragging, isDragOver]);

  const cableWidth = useMemo(
    () => getCableWidthForTessera(representativeData.sezione, settings.useAWG),
    [representativeData.sezione, settings.useAWG]
  );

  const handleClick = useCallback(() => {
    if (clickable && onCableClick) {
      onCableClick(displaySiglatura, cable.originalIndex);
    }
  }, [clickable, onCableClick, displaySiglatura, cable.originalIndex]);

  const handleEdit = useCallback((e) => {
    e.stopPropagation();
    if (onToggleDelete) onToggleDelete();

    if (cable.isMultipolarPoleEndpoint) {
      onEditMultipolarCable?.(cable.parentMultipolarIndex ?? cable.originalIndex);
    } else {
      onEditCable(cable.originalIndex);
    }
  }, [onToggleDelete, onEditCable, onEditMultipolarCable, cable]);

  const handleDelete = useCallback((e) => {
    e.stopPropagation();

    if (cable.isMultipolarPoleEndpoint) {
      onDeleteMultipolarCable(cable.parentMultipolarIndex ?? cable.originalIndex);
    } else {
      onDeleteCable(cable.originalIndex);
    }
  }, [onDeleteCable, onDeleteMultipolarCable, cable]);

  const renderConnectedCable = useCallback((connectedCable, nestedState) => (
    <CableTessera
      cable={connectedCable}
      validateCable={validateCable}
      displaySiglatura={getCableDisplayName(connectedCable, settings.lingua, settings)}
      onCableClick={onCableClick}
      onEditCable={onEditCable}
      onEditMultipolarCable={onEditMultipolarCable}
      onDeleteCable={onDeleteCable}
      onDeleteMultipolarCable={onDeleteMultipolarCable}
      showSiglatura={true}
      clickable={clickable}
      settings={settings}
      isDragEnabled={isDragEnabled}
      isDragging={nestedState.isDragging}
      isDragOver={nestedState.isDragOver}
      editingIndex={editingIndex}
      editingMultipolarIndex={editingMultipolarIndex}
      showDeleteButton={showDeleteButton}
      onToggleDelete={onToggleDelete}
      isSelectionModeEnabled={isSelectionModeEnabled}
      selectedCables={selectedCables}
      onToggleSelection={onToggleSelection}
      handleDragStart={handleDragStart}
      handleDragEnd={handleDragEnd}
      handleDragOver={handleDragOver}
      handleDragEnter={handleDragEnter}
      handleDragLeave={handleDragLeave}
      handleDrop={handleDrop}
      draggedIndex={draggedIndex}
      dragOverIndex={dragOverIndex}
      availableSheaths={availableSheaths}
      relatedCables={nestedState.relatedCables}
      terminalGroups={terminalGroups}
    />
  ), [
    validateCable,
    settings,
    onCableClick,
    onEditCable,
    onEditMultipolarCable,
    onDeleteCable,
    onDeleteMultipolarCable,
    clickable,
    isDragEnabled,
    editingIndex,
    editingMultipolarIndex,
    showDeleteButton,
    onToggleDelete,
    isSelectionModeEnabled,
    selectedCables,
    onToggleSelection,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    draggedIndex,
    dragOverIndex,
    availableSheaths,
    terminalGroups
  ]);

  // Il chip segnala che questo lato finisce in un morsetto condiviso con altri cavi.
  const renderTerminalGroupChip = (side) => {
    const match = getTerminalGroupForEndpoint(terminalGroups, cable, side);
    if (!match) return null;

    return (
      <TerminalGroupChip
        groupIndex={match.groupIndex}
        group={match.group}
        lingua={settings?.lingua}
        useAWG={settings?.useAWG}
        className="mr-1"
      />
    );
  };

  if (isMultipolar) {
    return (
      <MultipolarTessera
        cable={cable}
        clickable={clickable}
        onCableClick={onCableClick}
        onEditMultipolarCable={onEditMultipolarCable}
        onDeleteMultipolarCable={onDeleteMultipolarCable}
        settings={settings}
        validateCable={validateCable}
        isDragEnabled={isDragEnabled}
        isDragging={isDragging}
        isDragOver={isDragOver}
        hasIssues={hasIssues}
        isHovered={isHovered}
        editingIndex={editingIndex}
        editingMultipolarIndex={editingMultipolarIndex}
        showDeleteButton={showDeleteButton}
        onToggleDelete={onToggleDelete}
        isSelectionModeEnabled={isSelectionModeEnabled}
        selectedCables={selectedCables}
        onToggleSelection={onToggleSelection}
        handleDragStart={handleDragStart}
        handleDragEnd={handleDragEnd}
        handleDragOver={handleDragOver}
        handleDragEnter={handleDragEnter}
        handleDragLeave={handleDragLeave}
        handleDrop={handleDrop}
        draggedIndex={draggedIndex}
        dragOverIndex={dragOverIndex}
        availableSheaths={availableSheaths}
        relatedCables={relatedCables}
        terminalGroups={terminalGroups}
        renderConnectedCable={renderConnectedCable}
      />
    );
  }

  return (
    <div className={`${containerClasses} p-3 flex items-center justify-between relative group`} onClick={handleClick}>
      <div className={`flex items-center gap-3 flex-1 min-w-0 ${isDragEnabled ? 'ml-8' : ''}`}>
        {isSelectionModeEnabled && (
          <label className="flex items-center cursor-pointer flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={selectedCables.has(cable.originalIndex)}
              onChange={() => onToggleSelection?.(cable.originalIndex)}
              className="w-5 h-5 text-green-600 border-2 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
            />
          </label>
        )}

        {!isDragEnabled && actualHasIssues && (
          <div className="flex-shrink-0">
            <WarningIcon
              issues={validation.issues}
              lingua={settings?.lingua}
              className="flex-shrink-0"
            />
          </div>
        )}

        <span className="relative flex-shrink-0">
          <span className={`block px-2 py-0.5 rounded text-sm font-medium ${actualHasIssues ? 'bg-amber-200 text-amber-800' : 'bg-blue-100 text-blue-800'}`}>
            #{cable.originalIndex + 1}
          </span>
          <CatalogAssignmentIndicator
            code={cable.catalogAssignment?.codiceInterno}
            description={cable._catalogDescription}
            status={catalogStatus}
          />
        </span>

        <CableColorRect
          color={representativeData.colore}
          width={cableWidth}
          height="32px"
          borderRadius="9999px"
          className="transition-all duration-200 flex-shrink-0"
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {showSiglatura && (
              <span className="font-medium">
                {settings.marcaturePerCollegamento
                  ? getCableDisplayName(cable, settings.lingua, settings)
                  : (displaySiglatura || representativeData.siglatura)}
              </span>
            )}
            <span className="text-sm text-gray-500 flex-shrink-0">
              {translateColorForDisplay(representativeData.colore, settings.lingua)} • {formatSezione(representativeData.sezione, settings.useAWG)} • {cable.lunghezza}mm
            </span>
          </div>

          <div className="text-sm text-gray-600 mt-1 truncate">
            {renderTerminalGroupChip('A')}
            <span className="font-medium">{representativeData.terminaleA}</span>
            <span className="mx-1">→</span>
            {renderTerminalGroupChip('B')}
            <span className="font-medium">{representativeData.terminaleB}</span>

            {!settings.marcaturePerCollegamento && (
              <>
                <span className="mx-2 text-gray-400">|</span>
                <span className="font-medium text-gray-700">{representativeData.collegamentoA || '—'}</span>
                <span className="mx-1">→</span>
                <span className="font-medium text-gray-700">{representativeData.collegamentoB || '—'}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {!hideSheathBadge && cable.guaina && (() => {
        const sheath = availableSheaths.find(s => s.id === cable.guaina);
        return sheath ? (
          <div className={`flex-shrink-0 ml-2 ${isBeingEdited ? 'hidden' : 'group-hover:hidden'}`}>
            <span className="px-2 py-0.5 bg-sky-100 text-sky-800 rounded text-xs font-medium whitespace-nowrap">
              {sheath.label}
            </span>
          </div>
        ) : null;
      })()}

      <div className={`items-center gap-1 flex-shrink-0 ml-2 ${isBeingEdited ? 'flex' : 'hidden group-hover:flex'}`}>
        {!shouldShowDelete ? (
          <button
            onClick={handleEdit}
            disabled={isDragEnabled || isSelectionModeEnabled || ((editingIndex >= 0 || editingMultipolarIndex >= 0) && !isBeingEdited)}
            className={`p-2 rounded transition-colors ${isDragEnabled || isSelectionModeEnabled || ((editingIndex >= 0 || editingMultipolarIndex >= 0) && !isBeingEdited)
              ? 'text-gray-300 cursor-not-allowed'
              : actualHasIssues
                ? 'text-amber-600 hover:bg-amber-100'
                : 'text-blue-600 hover:bg-blue-50'
              }`}
            title={
              isSelectionModeEnabled
                ? 'Disabilita la modalita selezione per modificare i cavi'
                : (editingIndex >= 0 || editingMultipolarIndex >= 0) && !isBeingEdited
                  ? 'Termina la modifica in corso prima di modificare questo cavo'
                  : 'Modifica cavo'
            }
          >
            <Icons.Edit3 className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={handleDelete}
            disabled={isDragEnabled}
            className={`p-2 rounded transition-colors ${isDragEnabled ? 'text-gray-300 cursor-not-allowed' : 'text-red-600 hover:bg-red-50'}`}
            title="Cancella cavo definitivamente"
          >
            <Icons.Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  const prevIsMultipolar = prevProps.cable.poli && Array.isArray(prevProps.cable.poli);
  const nextIsMultipolar = nextProps.cable.poli && Array.isArray(nextProps.cable.poli);

  if (prevIsMultipolar !== nextIsMultipolar) return false;

  if (nextIsMultipolar) {
    return (
      prevProps.cable.originalIndex === nextProps.cable.originalIndex &&
      prevProps.cable.nomeCavo === nextProps.cable.nomeCavo &&
      prevProps.cable.lunghezza === nextProps.cable.lunghezza &&
      prevProps.cable.gruppo === nextProps.cable.gruppo &&
      JSON.stringify(prevProps.cable.catalogAssignment) === JSON.stringify(nextProps.cable.catalogAssignment) &&
      prevProps.cable._catalogStatus === nextProps.cable._catalogStatus &&
      prevProps.cable._catalogDescription === nextProps.cable._catalogDescription &&
      JSON.stringify(prevProps.cable.poli) === JSON.stringify(nextProps.cable.poli) &&
      prevProps.isDragEnabled === nextProps.isDragEnabled &&
      prevProps.isDragging === nextProps.isDragging &&
      prevProps.isDragOver === nextProps.isDragOver &&
      prevProps.hasIssues === nextProps.hasIssues &&
      prevProps.editingIndex === nextProps.editingIndex &&
      prevProps.editingMultipolarIndex === nextProps.editingMultipolarIndex &&
      prevProps.showDeleteButton === nextProps.showDeleteButton &&
      prevProps.settings.marcaturePerCollegamento === nextProps.settings.marcaturePerCollegamento &&
      prevProps.settings.useAWG === nextProps.settings.useAWG &&
      getRelatedCablesMemoKey(prevProps.relatedCables) === getRelatedCablesMemoKey(nextProps.relatedCables) &&
      prevProps.validateCable === nextProps.validateCable
    );
  }

  return (
    prevProps.cable.originalIndex === nextProps.cable.originalIndex &&
    prevProps.cable.siglatura === nextProps.cable.siglatura &&
    prevProps.cable.colore === nextProps.cable.colore &&
    prevProps.cable.sezione === nextProps.cable.sezione &&
    prevProps.cable.lunghezza === nextProps.cable.lunghezza &&
    prevProps.cable.terminaleA === nextProps.cable.terminaleA &&
    prevProps.cable.terminaleB === nextProps.cable.terminaleB &&
    prevProps.cable.collegamentoA === nextProps.cable.collegamentoA &&
    prevProps.cable.collegamentoB === nextProps.cable.collegamentoB &&
    JSON.stringify(prevProps.cable.catalogAssignment) === JSON.stringify(nextProps.cable.catalogAssignment) &&
    prevProps.cable._catalogStatus === nextProps.cable._catalogStatus &&
    prevProps.cable._catalogDescription === nextProps.cable._catalogDescription &&
    prevProps.displaySiglatura === nextProps.displaySiglatura &&
    prevProps.showSiglatura === nextProps.showSiglatura &&
    prevProps.clickable === nextProps.clickable &&
    prevProps.isDragEnabled === nextProps.isDragEnabled &&
    prevProps.isDragging === nextProps.isDragging &&
    prevProps.isDragOver === nextProps.isDragOver &&
    prevProps.hasIssues === nextProps.hasIssues &&
    prevProps.isHovered === nextProps.isHovered &&
    prevProps.editingIndex === nextProps.editingIndex &&
    prevProps.editingMultipolarIndex === nextProps.editingMultipolarIndex &&
    prevProps.showDeleteButton === nextProps.showDeleteButton &&
    prevProps.isSelectionModeEnabled === nextProps.isSelectionModeEnabled &&
    (prevProps.selectedCables?.size || 0) === (nextProps.selectedCables?.size || 0) &&
    (!prevProps.selectedCables || !nextProps.selectedCables ||
      [...prevProps.selectedCables].every(item => nextProps.selectedCables.has(item))) &&
    prevProps.onToggleSelection === nextProps.onToggleSelection &&
    prevProps.settings.marcaturePerCollegamento === nextProps.settings.marcaturePerCollegamento &&
    prevProps.settings.lingua === nextProps.settings.lingua &&
    prevProps.validateCable === nextProps.validateCable
  );
});

export default CableTessera;
